/**
 * Секрет входа через соцсеть в нативной оболочке (PKCE-схема).
 *
 * Раньше после входа в системном браузере сессионный токен приходил в приложение прямо в адресе
 * `gg.teamplay.twa://oauth-callback?token=…`. Такая схема не подтверждена владельцем домена, и
 * другое приложение на телефоне может объявить ту же и забрать токен. Теперь приложение создаёт
 * секрет ДО открытия браузера, серверу отдаёт только его хеш, а в адрес возврата приходит
 * короткий одноразовый код: без секрета его не обменять на токен.
 */
const VERIFIER_KEY = 'tp:native-oauth-verifier';

/** Секрет нужен, пока человек входит у провайдера: дольше входа он не живёт. */
const MAX_AGE_MS = 15 * 60 * 1000;

type StoredVerifier = { verifier: string; savedAt: number };

const base64Url = (bytes: Uint8Array) => {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
};

/**
 * Секрет и его хеш. Null, когда WebView не умеет считать SHA-256 (`crypto.subtle` есть только в
 * защищённом контексте): вход тогда идёт по прежней схеме с токеном в адресе, а не ломается.
 */
export const createPkcePair = async (): Promise<{ verifier: string; challenge: string } | null> => {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.subtle || !cryptoApi.getRandomValues) {
    return null;
  }
  try {
    const verifier = base64Url(cryptoApi.getRandomValues(new Uint8Array(32)));
    const digest = await cryptoApi.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return { verifier, challenge: base64Url(new Uint8Array(digest)) };
  } catch {
    return null;
  }
};

/**
 * Хранится в `localStorage`, а не в `sessionStorage`: Android выгружает приложение, пока человек
 * в браузере, и после возврата процесс новый.
 */
export const rememberPkceVerifier = (verifier: string) => {
  try {
    const payload: StoredVerifier = { verifier, savedAt: Date.now() };
    globalThis.localStorage?.setItem(VERIFIER_KEY, JSON.stringify(payload));
  } catch {
    // Хранилище закрыто: обмен кода не состоится, человек повторит вход.
  }
};

const PENDING_KEY = 'tp:native-oauth-pending';

/** Вход начало это приложение: без метки токен из адреса возврата никто не ждёт. */
export const markNativeOAuthPending = () => {
  try {
    globalThis.localStorage?.setItem(PENDING_KEY, String(Date.now()));
  } catch {
    // Хранилище закрыто: возврат со старой схемой будет отклонён, человек повторит вход.
  }
};

export const isNativeOAuthPending = (): boolean => {
  try {
    const savedAt = Number(globalThis.localStorage?.getItem(PENDING_KEY));
    return Number.isFinite(savedAt) && savedAt > 0 && Date.now() - savedAt <= MAX_AGE_MS;
  } catch {
    return false;
  }
};

export const clearNativeOAuthPending = () => {
  try {
    globalThis.localStorage?.removeItem(PENDING_KEY);
  } catch {
    // Нечего очищать.
  }
};

/** Читается один раз: повторный вход создаёт новый секрет. */
export const takePkceVerifier = (): string | null => {
  let raw: string | null = null;
  try {
    raw = globalThis.localStorage?.getItem(VERIFIER_KEY) ?? null;
    globalThis.localStorage?.removeItem(VERIFIER_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as StoredVerifier;
    if (!parsed?.verifier || !parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      return null;
    }
    return parsed.verifier;
  } catch {
    return null;
  }
};
